import React from 'react';

/**
 * Custom Confirm Dialog Component
 * Replaces native window.confirm() with a styled modal
 * 
 * Usage:
 * const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
 * const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
 * const handleConfirm = () => { confirm.onConfirm(); setConfirm({ show: false, text: '', onConfirm: null }); };
 * const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });
 * 
 * <ConfirmDialog confirm={confirm} onConfirm={handleConfirm} onCancel={handleCancel} />
 */
export default function ConfirmDialog({ confirm, onConfirm, onCancel, zIndex = 9999 }) {
  if (!confirm.show) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: zIndex || 9999,
      backdropFilter: 'blur(2px)'
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        border: '3px solid #2196F3',
        animation: 'confirmDialogFadeIn 0.2s ease-out'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            background: '#E3F2FD',
            color: '#2196F3'
          }}>
            ?
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#2196F3'
          }}>
            Confirm Action
          </h3>
        </div>
        <p style={{
          margin: '0 0 25px 0',
          fontSize: '16px',
          lineHeight: '1.5',
          color: '#333',
          whiteSpace: 'pre-wrap'
        }}>
          {confirm.text}
        </p>
        <div style={{
          display: 'flex',
          gap: '10px'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: 'white',
              color: '#2196F3',
              border: '2px solid #2196F3',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#E3F2FD';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'white';
            }}
          >
            {confirm.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onConfirm === 'function') onConfirm();
            }}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.opacity = '0.9'}
            onMouseOut={(e) => e.target.style.opacity = '1'}
          >
            {confirm.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes confirmDialogFadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
